// ContentBrowser — asset library + world content manager
// Fetches all user uploads from Supabase, shows which are in the world,
// lets you add any upload to the world or manage in-world items.
import React, { useState, useEffect } from 'react';
import { X, Trash2, Eye, EyeOff, Image, Music, Package, Upload, Plus, Check, Loader } from 'lucide-react';
import { supabase } from '../config/supabase';
import './ContentBrowser.css';

interface ContentItem {
  url: string;
  type: 'image' | 'audio' | 'model' | 'splat' | 'ply';
  position: [number, number, number];
  scale: number;
  rotation: [number, number, number];
  visible: boolean;
  filename?: string;
  uploadedAt?: number;
}

interface UploadRecord {
  id: string;
  name: string;
  url: string;
  thumbnail_url?: string | null;
  file_size: number;
  mime_type: string;
  metadata: { extension?: string; uploadedAt?: number };
}

interface ContentBrowserProps {
  contentItems: { [id: string]: ContentItem };
  user: any;
  onClose: () => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onSelect: (id: string) => void;
  onAdd: (url: string, filename: string, fileType: ContentItem['type']) => void;
  onUpload?: () => void;
  selectedId?: string | null;
}

type FilterType = 'all' | 'image' | 'audio' | 'model' | 'splat';

function extToType(ext?: string): ContentItem['type'] {
  if (!ext) return 'model';
  const e = ext.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif'].includes(e)) return 'image';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(e)) return 'audio';
  if (['spz', 'splat', 'ksplat'].includes(e)) return 'splat';
  if (e === 'ply') return 'ply';
  return 'model'; // glb, gltf, etc.
}

const ContentBrowser: React.FC<ContentBrowserProps> = ({
  contentItems,
  user,
  onClose,
  onDelete,
  onToggleVisibility,
  onSelect,
  onAdd,
  onUpload,
  selectedId,
}) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);

  // Build a set of URLs already in the world for quick lookup
  const inWorldUrls = new Set(Object.values(contentItems).map((i) => i.url));

  // Also collect in-world items by URL → id
  const urlToId: Record<string, string> = {};
  Object.entries(contentItems).forEach(([id, item]) => { urlToId[item.url] = id; });

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }

    supabase
      .from('user_uploads')
      .select('id, name, url, thumbnail_url, file_size, mime_type, metadata')
      .eq('user_id', user.id)
      .eq('type', 'content')
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) console.error('[ContentBrowser] Failed to load uploads:', error);
        setUploads(data || []);
        setLoading(false);
      });
  }, [user?.id]);

  const getFileExtension = (url: string) => {
    return url.split('.').pop()?.toUpperCase() || '?';
  };

  const formatDate = (ts?: number) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Colored type badge for the thumbnail placeholder
  const TypePreview: React.FC<{ ext: string; type: ContentItem['type'] | 'ply' }> = ({ ext, type }) => {
    const configs: Record<string, { bg: string; color: string; icon: React.ReactNode; label: string }> = {
      image:  { bg: 'rgba(34,197,94,0.15)',   color: '#4ade80', icon: <Image size={32} />,   label: ext },
      audio:  { bg: 'rgba(251,146,60,0.15)',   color: '#fb923c', icon: <Music size={32} />,   label: ext },
      model:  { bg: 'rgba(99,102,241,0.15)',   color: '#818cf8', icon: <Package size={32} />, label: ext },
      splat:  { bg: 'rgba(0,194,179,0.15)',    color: '#00c2b3', icon: <Package size={32} />, label: ext },
      ply:    { bg: 'rgba(168,85,247,0.15)',   color: '#c084fc', icon: <Package size={32} />, label: ext },
    };
    const cfg = configs[type] || configs.model;
    return (
      <div style={{
        width: '100%', height: 120,
        background: cfg.bg,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 6,
        color: cfg.color,
      }}>
        {cfg.icon}
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, opacity: 0.8 }}>{cfg.label}</span>
      </div>
    );
  };

  const filtered = uploads.filter((u) => {
    if (filter === 'all') return true;
    const t = extToType(u.metadata?.extension);
    if (filter === 'splat') return t === 'splat' || t === 'ply';
    return t === filter;
  });

  const handleAdd = async (upload: UploadRecord) => {
    const fileType = extToType(upload.metadata?.extension);
    setAdding(upload.id);
    onAdd(upload.url, upload.name, fileType);
    // Small delay so the user sees the "adding" state
    setTimeout(() => setAdding(null), 600);
  };

  return (
    <div className="content-browser-overlay">
      <div className="content-browser">
        <div className="content-browser-header">
          <h2>My Uploads</h2>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {onUpload && (
              <button className="content-upload-btn" onClick={onUpload}>
                <Upload size={16} /> Upload New
              </button>
            )}
            <button className="content-browser-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="content-browser-filters">
          {(['all', 'image', 'model', 'audio', 'splat'] as FilterType[]).map((f) => (
            <button
              key={f}
              className={`filter-btn ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' && `All (${uploads.length})`}
              {f === 'image' && <><Image size={14} /> Images</>}
              {f === 'model' && <><Package size={14} /> Models</>}
              {f === 'audio' && <><Music size={14} /> Audio</>}
              {f === 'splat' && <><Package size={14} /> Splats</>}
            </button>
          ))}
        </div>

        <div className="content-browser-grid">
          {loading ? (
            <div className="content-browser-empty">
              <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <p>Loading library…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="content-browser-empty">
              <p>No uploads found</p>
              {onUpload && (
                <button className="content-upload-btn" onClick={onUpload} style={{ marginTop: 8 }}>
                  <Upload size={16} /> Upload Content
                </button>
              )}
            </div>
          ) : (
            filtered.map((upload) => {
              const fileType = extToType(upload.metadata?.extension);
              const isInWorld = inWorldUrls.has(upload.url);
              const worldId = urlToId[upload.url];
              const worldItem = worldId ? contentItems[worldId] : null;
              const isAdding = adding === upload.id;

              return (
                <div
                  key={upload.id}
                  className={`content-item ${worldId && selectedId === worldId ? 'selected' : ''} ${worldItem && !worldItem.visible ? 'hidden' : ''}`}
                  onClick={() => { if (isInWorld && worldId) onSelect(worldId); }}
                  style={{ cursor: isInWorld ? 'pointer' : 'default' }}
                >
                  {/* Thumbnail or type preview */}
                  <div className="content-item-thumbnail-wrap">
                    {upload.thumbnail_url || fileType === 'image' ? (
                      <div className="content-thumbnail" style={{ backgroundImage: `url(${upload.thumbnail_url || upload.url})` }} />
                    ) : (
                      <TypePreview ext={getFileExtension(upload.url)} type={fileType} />
                    )}
                  </div>

                  <div className="content-item-info">
                    <div className="content-item-name" title={upload.name}>
                      {upload.name}
                    </div>
                    <div className="content-item-meta">
                      <div className="content-item-meta-row">
                        <span className="content-item-type">{getFileExtension(upload.url)}</span>
                        {upload.file_size > 0 && (
                          <span style={{ opacity: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(upload.file_size / 1024 / 1024).toFixed(1)} MB
                          </span>
                        )}
                      </div>
                      {upload.metadata?.uploadedAt && (
                        <span className="content-item-meta-date">
                          {formatDate(upload.metadata.uploadedAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Hover overlay: visibility + delete for in-world items */}
                  {isInWorld && (
                    <div className="content-item-actions">
                      <button
                        className="content-action-btn"
                        onClick={(e) => { e.stopPropagation(); if (worldId) onToggleVisibility(worldId); }}
                        title={worldItem?.visible ? 'Hide' : 'Show'}
                      >
                        {worldItem?.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button
                        className="content-action-btn content-action-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (worldId && window.confirm('Remove from world?')) onDelete(worldId);
                        }}
                        title="Remove from world"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}

                  {/* Bottom bar: always visible */}
                  <div className="content-item-footer">
                    {isInWorld ? (
                      <span className="content-in-world-badge">In World</span>
                    ) : (
                      <button
                        className={`content-add-btn${isAdding ? ' adding' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleAdd(upload); }}
                        disabled={isAdding}
                      >
                        {isAdding ? <Check size={13} /> : <Plus size={13} />}
                        {isAdding ? 'Added' : 'Add to World'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ContentBrowser;
